/**
 * Runtime UI configuration (AUTH-120).
 *
 * Replaces the legacy build-time `VITE_*` env vars. The auth API serves these
 * values at /auth.getPublicConfig — fetched once at root-route load via
 * TanStack Query and provided to the rest of the app through React context.
 *
 * Why runtime instead of build-time:
 * a single Docker image works in test/staging/production. The trusted-domain
 * value is per-environment (e.g. `withvisor.com` in test/staging,
 * `betterbookkeeping.com` in production), and baking it at build time meant
 * promoting an image across envs silently broke redirect validation.
 */
import { createContext, useContext } from "react";
import { queryOptions } from "@tanstack/react-query";
import { orpcClient } from "./auth-client";

export interface RuntimeConfig {
  /** Domain suffix used for wildcard subdomain trust (e.g. "withvisor.com"). */
  trustedDomain: string | null;
  /**
   * Non-opinionated client service URLs, resolved per environment server-side.
   * The UI owns the redirect decision (new signups → dashboard, existing
   * logins / "go to app" → app).
   */
  services: {
    /** Old client app (app.*). */
    app: string | null;
    /** New client dashboard (dashboard.*). */
    dashboard: string | null;
  };
  /** Additional fully-qualified origins to trust. */
  trustedOrigins: string[];
  /** Public Cloudflare Turnstile widget key. Null when signup protection is disabled. */
  turnstileSiteKey: string | null;
  /** The auth gateway's own origin — derived from window.location at load time. */
  baseOrigin: string;
}

export const runtimeConfigQueryOptions = queryOptions({
  queryKey: ["runtime-config"] as const,
  queryFn: async (): Promise<RuntimeConfig> => {
    const config = await orpcClient.auth.getPublicConfig();
    return {
      trustedDomain: config.trustedDomain,
      services: config.services,
      trustedOrigins: config.trustedOrigins,
      turnstileSiteKey: config.turnstileSiteKey,
      baseOrigin: typeof window !== "undefined" ? window.location.origin : "",
    };
  },
  staleTime: Infinity,
  gcTime: Infinity,
});

export const RuntimeConfigContext = createContext<RuntimeConfig | null>(null);

export function useRuntimeConfig(): RuntimeConfig {
  const ctx = useContext(RuntimeConfigContext);
  if (!ctx) {
    throw new Error("useRuntimeConfig must be used inside RuntimeConfigContext.Provider");
  }
  return ctx;
}
